import { useMemo, useState } from "react";
import { toast } from "sonner";
import { ChevronDown, ChevronRight, ArrowUpDown, ArrowUp, ArrowDown, Calendar, CheckCircle2 } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { resolveAssignees } from "../lib/assignees";
import { AssigneeAvatars } from "./AssigneeAvatars";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "./ui/dropdown-menu";

const PRIORITY = {
  high: { label: "Yüksek", cls: "text-red-600 bg-red-50 dark:bg-red-500/15 dark:text-red-400" },
  medium: { label: "Orta", cls: "text-orange-600 bg-orange-50 dark:bg-orange-500/15 dark:text-orange-400" },
  low: { label: "Düşük", cls: "text-blue-600 bg-blue-50 dark:bg-blue-500/15 dark:text-blue-400" },
};
const PRIO_RANK = { high: 0, medium: 1, low: 2 };
const fmt = (d) => (d ? new Date(d + "T00:00:00").toLocaleDateString("tr-TR", { day: "numeric", month: "short" }) : "—");
const COLS = [
  { key: "title", label: "Görev", cls: "flex-1 min-w-0" },
  { key: "status", label: "Statü", cls: "w-36 shrink-0" },
  { key: "assignees", label: "Atananlar", cls: "w-28 shrink-0", sortable: false },
  { key: "start_date", label: "Başlangıç", cls: "w-24 shrink-0" },
  { key: "due_date", label: "Bitiş", cls: "w-24 shrink-0" },
  { key: "priority", label: "Öncelik", cls: "w-24 shrink-0" },
  { key: "progress", label: "Alt görev", cls: "w-24 shrink-0" },
];

const StatusPill = ({ task, columns, onChange }) => {
  const col = columns.find((c) => c.id === task.column_id);
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button data-testid="list-status-pill" onClick={(e) => e.stopPropagation()}
          className="h-7 pl-2 pr-2.5 rounded-full text-[11px] font-semibold flex items-center gap-1.5 border transition-colors hover:brightness-95"
          style={{ background: `${col?.color || "#8E8E93"}18`, color: col?.color || "#8E8E93", borderColor: `${col?.color || "#8E8E93"}40` }}>
          <span className="w-1.5 h-1.5 rounded-full" style={{ background: col?.color || "#8E8E93" }} />
          <span className="truncate max-w-[92px]">{col?.name || "—"}</span>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="rounded-xl min-w-[170px]" onClick={(e) => e.stopPropagation()}>
        {columns.map((c) => (
          <DropdownMenuItem key={c.id} data-testid="list-status-option" onClick={() => c.id !== task.column_id && onChange({ column_id: c.id })} className="rounded-lg text-sm gap-2">
            <span className="w-2 h-2 rounded-full" style={{ background: c.color }} /> {c.name}
            {c.id === task.column_id && <CheckCircle2 size={13} className="ml-auto text-slate-400" />}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

const PriorityPill = ({ task, onChange }) => {
  const p = PRIORITY[task.priority] || PRIORITY.medium;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button data-testid="list-priority-pill" onClick={(e) => e.stopPropagation()} className={`h-6 px-2 rounded-md text-[10px] font-bold uppercase tracking-wide ${p.cls} hover:brightness-95 transition-all`}>{p.label}</button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="rounded-xl min-w-[130px]" onClick={(e) => e.stopPropagation()}>
        {Object.entries(PRIORITY).map(([k, v]) => (
          <DropdownMenuItem key={k} data-testid="list-priority-option" onClick={() => k !== task.priority && onChange({ priority: k })} className="rounded-lg text-sm">
            <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold uppercase ${v.cls}`}>{v.label}</span>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

const Row = ({ task, board, onClick, onPatch }) => {
  const subs = task.subtasks || [];
  const done = subs.filter((s) => s.done).length;
  const overdue = task.due_date && !board.columns.find((c) => c.id === task.column_id)?.is_done && task.due_date < new Date().toISOString().slice(0, 10);
  return (
    <div data-testid="list-task-row" onClick={onClick}
      className="group flex items-center gap-3 px-4 h-12 border-t border-slate-100 dark:border-white/5 hover:bg-[#007AFF]/[0.04] dark:hover:bg-white/[0.04] cursor-pointer transition-colors">
      <div className={COLS[0].cls}>
        <p className="text-sm font-medium text-slate-800 dark:text-slate-100 truncate">{task.title}</p>
      </div>
      <div className={COLS[1].cls}><StatusPill task={task} columns={board.columns} onChange={onPatch} /></div>
      <div className={COLS[2].cls}><AssigneeAvatars assignees={resolveAssignees(task, board)} size="sm" max={3} /></div>
      <div className={`${COLS[3].cls} text-xs text-slate-500 dark:text-slate-400 flex items-center gap-1`}><Calendar size={11} className="opacity-60" /> {fmt(task.start_date)}</div>
      <div className={`${COLS[4].cls} text-xs flex items-center gap-1 ${overdue ? "text-[#FF2D55] font-semibold" : "text-slate-500 dark:text-slate-400"}`}>{fmt(task.due_date)}</div>
      <div className={COLS[5].cls}><PriorityPill task={task} onChange={onPatch} /></div>
      <div className={`${COLS[6].cls} flex items-center gap-2`}>
        {subs.length > 0 ? (
          <>
            <div className="flex-1 h-1.5 rounded-full bg-slate-200 dark:bg-white/10 overflow-hidden"><div className="h-full bg-[#34C759] rounded-full transition-all" style={{ width: `${(done / subs.length) * 100}%` }} /></div>
            <span className="text-[10px] text-slate-500 tabular-nums">{done}/{subs.length}</span>
          </>
        ) : <span className="text-[10px] text-slate-300 dark:text-slate-600">—</span>}
      </div>
    </div>
  );
};

export const ListView = ({ board, visibleIds, onTaskClick, reload }) => {
  const [sort, setSort] = useState({ key: "start_date", dir: "asc" });
  const [collapsed, setCollapsed] = useState({});

  const patch = async (task, updates) => {
    try {
      await api.patch(`/tasks/${task.id}`, updates);
      reload();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const groups = useMemo(() => {
    const colOrder = Object.fromEntries(board.columns.map((c, i) => [c.id, i]));
    const cmp = (a, b) => {
      const k = sort.key;
      let va, vb;
      if (k === "status") { va = colOrder[a.column_id] ?? 99; vb = colOrder[b.column_id] ?? 99; }
      else if (k === "priority") { va = PRIO_RANK[a.priority] ?? 1; vb = PRIO_RANK[b.priority] ?? 1; }
      else if (k === "progress") { va = (a.subtasks || []).filter((s) => s.done).length / ((a.subtasks || []).length || 1); vb = (b.subtasks || []).filter((s) => s.done).length / ((b.subtasks || []).length || 1); }
      else { va = a[k] || ""; vb = b[k] || ""; }
      const r = typeof va === "string" ? va.localeCompare(vb, "tr") : va < vb ? -1 : va > vb ? 1 : 0;
      return sort.dir === "asc" ? r : -r;
    };
    const visible = board.tasks.filter((t) => !visibleIds || visibleIds.has(t.id));
    const earliest = {};
    board.tasks.forEach((t) => {
      const g = t.group || "Genel";
      const d = t.start_date || t.due_date || "9999";
      if (!earliest[g] || d < earliest[g]) earliest[g] = d;
    });
    const names = Object.keys(earliest).sort((a, b) => earliest[a].localeCompare(earliest[b]) || a.localeCompare(b, "tr"));
    return names.map((name) => {
      const all = board.tasks.filter((t) => (t.group || "Genel") === name);
      const tasks = visible.filter((t) => (t.group || "Genel") === name).sort(cmp);
      return { name, tasks, total: all.length, shown: tasks.length, done: tasks.filter((t) => board.columns.find((c) => c.id === t.column_id)?.is_done).length };
    }).filter((g) => g.tasks.length > 0);
  }, [board, visibleIds, sort]);

  const toggleSort = (key) => setSort((s) => (s.key === key ? { key, dir: s.dir === "asc" ? "desc" : "asc" } : { key, dir: "asc" }));
  const SortIcon = ({ col }) => sort.key !== col ? <ArrowUpDown size={11} className="opacity-40" /> : sort.dir === "asc" ? <ArrowUp size={11} /> : <ArrowDown size={11} />;

  return (
    <div data-testid="list-view" className="bg-white/90 dark:bg-[#1C1C1E]/90 backdrop-blur-md rounded-2xl border border-slate-200/70 dark:border-white/10 shadow-[0_4px_20px_-4px_rgba(0,0,0,0.05)] overflow-hidden animate-fade-up">
      <div className="flex items-center gap-3 px-4 h-10 bg-slate-50/80 dark:bg-white/5 text-[11px] font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
        {COLS.map((c) => (
          <button key={c.key} data-testid={`list-sort-${c.key}`} disabled={c.sortable === false} onClick={() => toggleSort(c.key)}
            className={`${c.cls} flex items-center gap-1 text-left hover:text-slate-800 dark:hover:text-white disabled:hover:text-inherit transition-colors ${sort.key === c.key ? "text-[#0066CC] dark:text-[#5AA9FF]" : ""}`}>
            {c.label} {c.sortable !== false && <SortIcon col={c.key} />}
          </button>
        ))}
      </div>
      {groups.map((g, gi) => {
        const open = !collapsed[g.name];
        return (
          <div key={g.name} data-testid="list-group">
            <button data-testid="list-group-toggle" onClick={() => setCollapsed((c) => ({ ...c, [g.name]: open }))}
              className="w-full flex items-center gap-2 px-4 h-11 border-t border-slate-100 dark:border-white/5 bg-slate-50/50 dark:bg-white/[0.03] hover:bg-slate-100/60 dark:hover:bg-white/[0.06] transition-colors text-left">
              {open ? <ChevronDown size={14} className="text-slate-400" /> : <ChevronRight size={14} className="text-slate-400" />}
              <span className="w-2 h-2 rounded-full" style={{ background: ["#007AFF", "#AF52DE", "#FF9500", "#30B0C7", "#34C759", "#FF2D55", "#5856D6", "#FFCC00"][gi % 8] }} />
              <span className="text-sm font-bold text-slate-800 dark:text-slate-100 tracking-tight truncate">{g.name}</span>
              <span className="text-[11px] text-slate-400 ml-1">{g.shown < g.total ? `${g.shown}/${g.total} görev` : `${g.total} görev`}</span>
              <span className="ml-auto text-[10px] font-semibold text-slate-500 dark:text-slate-400 bg-white dark:bg-white/10 rounded-full px-2 py-0.5 border border-slate-200/70 dark:border-white/10">{g.done}/{g.shown} tamam</span>
            </button>
            {open && g.tasks.map((t) => <Row key={t.id} task={t} board={board} onClick={() => onTaskClick(t)} onPatch={(u) => patch(t, u)} />)}
          </div>
        );
      })}
      {groups.length === 0 && <div data-testid="list-empty" className="py-16 text-center text-sm text-slate-400">Filtreye uyan görev yok.</div>}
    </div>
  );
};

export default ListView;
